import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import jsPDF from "jspdf"
import "jspdf-autotable"
import { usePermissions } from "API/PermissionContext"
import classnames from "classnames"

const ProcurementAttendance = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS
  const [activeTab, setActiveTab] = useState("1")
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [results, setResults] = useState([])
  const [allCenters, setAllCenters] = useState([])
  const [centers, setCenters] = useState([])
  const [routes, setRoutes] = useState([])
  const [allPlants, setAllPlants] = useState([])
  const [plants, setPlants] = useState([])
  const [collectionCenters, setCollectionCenters] = useState([])
  const [producers, setProducers] = useState([])
  const [vendors, setVendors] = useState([])
  const [companies, setCompanies] = useState([])

  const today = new Date().toISOString().split("T")[0]

  useEffect(() => { loadData() }, [])
  useEffect(() => { setSubmitted(false); setResults([]); setRoutes([]) }, [activeTab])

  const loadData = async () => {
    try {
      const [cResp, pResp, ccResp, prResp, vResp, coResp] = await Promise.all([
        api.get("centers"), api.get("plants"),
        api.get("collection-centers"), api.get("producers"), api.get("vendors"), api.get("companies")
      ])
      const ccData = Array.isArray(cResp.data.data) ? cResp.data.data : cResp.data.data?.data || []
      setAllCenters(ccData)
      setCenters(ccData)
      const pData = Array.isArray(pResp.data.data) ? pResp.data.data : pResp.data.data?.data || []
      setAllPlants(pData)
      setPlants(pData)
      setCollectionCenters(Array.isArray(ccResp.data.data) ? ccResp.data.data : ccResp.data.data?.data || [])
      setProducers(Array.isArray(prResp.data.data) ? prResp.data.data : prResp.data.data?.data || [])
      setVendors(Array.isArray(vResp.data.data) ? vResp.data.data : vResp.data.data?.data || [])
      setCompanies(Array.isArray(coResp.data.data) ? coResp.data.data : coResp.data.data?.data || [])
    } catch (e) { console.error(e) }
  }

  const companyChange = async e => {
    const val = e.target.value
    setCenters(allCenters); setRoutes([]); setCollectionCenters([])
    if (val && val !== "") {
      try { const r = await api.get("plants-by-company/" + val); setPlants(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){}
    } else setPlants(allPlants)
  }

  const plantChange = async e => {
    const val = e.target.value
    setRoutes([]); setCollectionCenters([])
    if (val && val !== "") {
      try {
        const r = await api.get("chillings-by-plant/" + val)
        setCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      } catch (e) { console.error(e) }
    } else {
      setCenters(allCenters)
    }
  }

  const ccChange = async e => {
    try {
      const val = e.target.value
      const u = !val || val === "0" ? "routes" : "route-cc/" + val
      const r = await api.get(u)
      setRoutes(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
    } catch (e) { console.error(e) }
  }

  const routeChange = async e => {
    try {
      const val = e.target.value
      if (val && val !== "0") {
        const r = await api.get("collection-center-route/" + val)
        setCollectionCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      } else {
        const r = await api.get("collection-centers")
        setCollectionCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      }
    } catch (e) { console.error(e) }
  }

  const centerChange = async e => {
    try {
      const val = e.target.value
      if (val && val !== "0") {
        const r = await api.get("producer-center/" + val)
        setProducers(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      } else {
        const r = await api.get("producers")
        setProducers(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      }
    } catch (e) { console.error(e) }
  }

  const isVlcc = activeTab === "1"
  const showCompany = companies.length > 1

  const handleSubmit = async (e, v) => {
    setLoading(true)
    try {
      const endpoint = isVlcc ? "reports/vlcc-purchase-absentees" : "reports/cc-purchase-absentees"
      const resp = await api.post(endpoint, v)
      if (resp.data.success) { setResults(resp.data.data || []); setSubmitted(true) }
      else { setResults([]); setSubmitted(true) }
    } catch (err) { console.error(err); setResults([]); setSubmitted(true) }
    setLoading(false)
  }

  const downloadPdf = () => {
    const label = isVlcc ? "VLCC Purchase Absentees Report" : "CC Purchase Absentees Report"
    const pdf = new jsPDF("l")
    pdf.setFontSize(14)
    pdf.text(label, 14, 15)
    pdf.autoTable({ html: "#absenteesTbl", startY: 22 })
    pdf.save(label.replace(/ /g, "-") + ".pdf")
  }

  const downloadCsv = () => {
    if (!results.length) return
    const label = isVlcc ? "VLCC-Purchase-Absentees" : "CC-Purchase-Absentees"
    let csv = ""
    if (isVlcc) {
      csv = "S.No,Producer,Producer No,Plant,Chilling Center,Route,Center,Period From,Period To\n"
      results.forEach((r, i) => {
        csv += `${i + 1},"${r.producerName}","${r.producerCode}","${r.plantName}","${r.ccName}","${r.routeName}","${r.centerName}","${r.periodFrom}","${r.periodTo}"\n`
      })
    } else {
      csv = "S.No,Vendor,Plant,Chilling Center,Route,Period From,Period To\n"
      results.forEach((r, i) => {
        csv += `${i + 1},"${r.vendorName}","${r.plantName}","${r.ccName}","${r.routeName}","${r.periodFrom}","${r.periodTo}"\n`
      })
    }
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = label + ".csv"
    link.click()
  }

  // ── VLCC Tab Form ──
  const renderVlccForm = () => (
    <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
      {showCompany && <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
      </Row>}
      <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant" onChange={plantChange}><option value="">All</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center" onChange={ccChange}><option value="">All</option>{centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="route" label="Route" onChange={routeChange}><option value="">All</option>{routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="center" label="Center" onChange={centerChange}><option value="">All</option>{collectionCenters.map((c, i) => <option key={i} value={c.id}>{c.centerName || c.name}</option>)}</AvField></div></Col>
      </Row>
      <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="producer" label="Producer"><option value="">All</option>{producers.map((p, i) => <option key={i} value={p.id}>{p.producerName || p.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField name="from" label="Period From *" type="date" value={today} required /></div></Col>
        <Col md={3}><div className="mb-3"><AvField name="to" label="Period To *" type="date" value={today} required /></div></Col>
        <Col md={3}><div className="mb-3"><AvRadioGroup inline name="shift" label="Shift *" value="both" required><AvRadio label="Both" value="both" /><AvRadio label="AM" value="AM" /><AvRadio label="PM" value="PM" /></AvRadioGroup></div></Col>
      </Row>
      <div className="mb-3 mt-1">
        <button className="btn btn-primary w-md me-2" type="submit" disabled={loading}>{loading ? <><Spinner size="sm" /> Submitting...</> : <><i className="fas fa-check me-1"></i>Submit</>}</button>
        {submitted && results.length > 0 && <button className="btn btn-info w-md" type="button" onClick={downloadCsv}><i className="fas fa-file-csv me-1"></i>CSV Download</button>}
      </div>
    </AvForm>
  )

  // ── CC Tab Form ──
  const renderCcForm = () => (
    <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
      {showCompany && <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
      </Row>}
      <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant *" onChange={plantChange}><option value="">Select</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center *" onChange={ccChange} required><option value="">Select</option>{centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="route" label="Route"><option value="">All</option>{routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="vendor" label="Vendor"><option value="">All</option>{vendors.map((v, i) => <option key={i} value={v.id}>{v.vendorName}</option>)}</AvField></div></Col>
      </Row>
      <Row>
        <Col md={3}><div className="mb-3"><AvField name="from" label="Period From *" type="date" value={today} required /></div></Col>
        <Col md={3}><div className="mb-3"><AvField name="to" label="Period To *" type="date" value={today} required /></div></Col>
        <Col md={3}><div className="mb-3"><AvRadioGroup inline name="shift" label="Shift *" value="both" required><AvRadio label="Both" value="both" /><AvRadio label="AM" value="AM" /><AvRadio label="PM" value="PM" /></AvRadioGroup></div></Col>
        <Col md={3} className="d-flex align-items-end pb-3">
          <button className="btn btn-primary w-md me-2" type="submit" disabled={loading}>{loading ? <><Spinner size="sm" /> Submitting...</> : <><i className="fas fa-check me-1"></i>Submit</>}</button>
          {submitted && results.length > 0 && <button className="btn btn-info w-md" type="button" onClick={downloadCsv}><i className="fas fa-file-csv me-1"></i>CSV Download</button>}
        </Col>
      </Row>
    </AvForm>
  )

  const renderVlccTable = () => (
    <Table id="absenteesTbl" className="dairyTbl" size="sm" bordered>
      <thead><tr>
        <th>S.No</th><th>Producer</th><th>Producer No</th><th>Plant</th><th>Chilling Center</th><th>Route</th><th>Center</th><th>Period From</th><th>Period To</th>
      </tr></thead>
      <tbody>
        {results.length > 0 ? results.map((r, i) => (
          <tr key={i}>
            <td>{i + 1}</td><td>{r.producerName}</td><td>{r.producerCode}</td><td>{r.plantName}</td><td>{r.ccName}</td><td>{r.routeName}</td><td>{r.centerName}</td><td>{r.periodFrom}</td><td>{r.periodTo}</td>
          </tr>
        )) : <tr><td colSpan="9" className="text-center py-3">No Absentees Found</td></tr>}
      </tbody>
      {results.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}><td colSpan="9">Total Absentees: {results.length}</td></tr></tfoot>}
    </Table>
  )

  const renderCcTable = () => (
    <Table id="absenteesTbl" className="dairyTbl" size="sm" bordered>
      <thead><tr>
        <th>S.No</th><th>Vendor</th><th>Plant</th><th>Chilling Center</th><th>Route</th><th>Period From</th><th>Period To</th>
      </tr></thead>
      <tbody>
        {results.length > 0 ? results.map((r, i) => (
          <tr key={i}>
            <td>{i + 1}</td><td>{r.vendorName}</td><td>{r.plantName}</td><td>{r.ccName}</td><td>{r.routeName}</td><td>{r.periodFrom}</td><td>{r.periodTo}</td>
          </tr>
        )) : <tr><td colSpan="7" className="text-center py-3">No Absentees Found</td></tr>}
      </tbody>
      {results.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}><td colSpan="7">Total Absentees: {results.length}</td></tr></tfoot>}
    </Table>
  )

  const tabLabel = isVlcc ? "VLCC Purchase Absentees Report" : "CC Purchase Absentees Report"

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader notToPrint"><Row><Col md={12}><h4>{tabLabel}</h4></Col></Row></div>
      <div className="dashContent">
        <Nav tabs className="nav-tabs-custom mb-3 notToPrint">
          <NavItem><NavLink className={classnames({ active: activeTab === "1" })} onClick={() => setActiveTab("1")} style={{ cursor: "pointer" }}>VLCC Absentees</NavLink></NavItem>
          <NavItem><NavLink className={classnames({ active: activeTab === "2" })} onClick={() => setActiveTab("2")} style={{ cursor: "pointer" }}>CC Absentees</NavLink></NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            {activeTab === "1" && <>
              {renderVlccForm()}
              {submitted && <>
                {canExport(MID) && <div className="exportBtns notToPrint mb-2">
                  <button className="btn btn-info btn-sm m-1" onClick={downloadPdf}><i className="fas fa-file-pdf me-1"></i>PDF</button>
                  <button className="btn btn-info btn-sm m-1" onClick={() => window.print()}><i className="fas fa-print me-1"></i>Print</button>
                </div>}
                <div className="table-responsive">{renderVlccTable()}</div>
              </>}
            </>}
          </TabPane>
          <TabPane tabId="2">
            {activeTab === "2" && <>
              {renderCcForm()}
              {submitted && <>
                {canExport(MID) && <div className="exportBtns notToPrint mb-2">
                  <button className="btn btn-info btn-sm m-1" onClick={downloadPdf}><i className="fas fa-file-pdf me-1"></i>PDF</button>
                  <button className="btn btn-info btn-sm m-1" onClick={() => window.print()}><i className="fas fa-print me-1"></i>Print</button>
                </div>}
                <div className="table-responsive">{renderCcTable()}</div>
              </>}
            </>}
          </TabPane>
        </TabContent>
      </div>
    </div>
  )
}
export default ProcurementAttendance
